import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "focusable", "accessibleWhenDisabled", "showOnHover"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useEffect } from 'react';
import { getPopupItemRole, getPopupRole } from 'ariakit-utils/dom';
import { useEvent, useId, useForkRef } from 'ariakit-utils/hooks';
import { useStore } from 'ariakit-utils/store';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useCompositeTypeahead } from '../composite/composite-typeahead.js';
import { useHovercardAnchor } from '../hovercard/hovercard-anchor.js';
import { usePopoverDisclosure } from '../popover/popover-disclosure.js';
import { M as MenuContext, a as MenuBarContext, h as hasExpandedMenuButton } from '../__utils-07f4a93f.js';

function getInitialFocus(event, dir) {
  var keyMap = {
    ArrowDown: dir === "bottom" || dir === "top" ? "first" : false,
    ArrowUp: dir === "bottom" || dir === "top" ? "last" : false,
    ArrowRight: dir === "right" ? "first" : false,
    ArrowLeft: dir === "left" ? "first" : false
  };
  return keyMap[event.key];
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu button that triggers a dropdown menu.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenuButton({ state });
 * <Role {...props}>Edit</Role>
 * <Menu state={state}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */


var useMenuButton = createHook(function (_ref) {
  var state = _ref.state,
      focusable = _ref.focusable,
      accessibleWhenDisabled = _ref.accessibleWhenDisabled,
      _showOnHover = _ref.showOnHover,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);
  var parentMenu = useStore(MenuContext, ["items", "move", "contentElement"]);
  var parentMenuBar = useStore(MenuBarContext, ["items", "move"]);
  var hasParentMenu = !!parentMenu;
  var parentIsMenuBar = !!parentMenuBar && !hasParentMenu;
  var disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  useEffect(function () {
    // Makes sure that the menu button is assigned as the menu disclosure
    // element. This is needed to support screen reader focusing on sibling
    // menu items.
    state.disclosureRef.current = ref.current;
  });
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return; // Reset the autoFocusOnShow state so we can focus the menu button while
    // the menu is open and press arrow keys to move focus to the menu items.

    state.setAutoFocusOnShow(false); // We need to unset the active menu item so no menu item appears active
    // while the menu button is focused.

    state.setActiveId(null); // When the menu button is focused, we'll only show its menu if it's in a
    // menu bar

    if (!parentMenuBar) return;
    if (!parentIsMenuBar) return; // and there's already another expanded menu button.

    if (hasExpandedMenuButton(parentMenuBar.items, event.currentTarget)) {
      state.show();
    }
  });
  var dir = state.placement.split("-")[0];
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    var initialFocus = getInitialFocus(event, dir);

    if (initialFocus) {
      event.preventDefault();
      state.show();
      state.setAutoFocusOnShow(true);
      state.setInitialFocus(initialFocus);
    }
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    var isKeyboardClick = !event.detail; // When the menu button is clicked, if the menu is hidden or if it's a
    // keyboard click (enter or space),

    if (!state.open || isKeyboardClick) {
      // we'll only automatically focus on the menu if it's not a submenu
      // button, or if it's a keyboard click.
      if (!hasParentMenu || isKeyboardClick) {
        state.setAutoFocusOnShow(true);
      }

      state.setInitialFocus(isKeyboardClick ? "first" : "container");
    } // On submenu buttons, we can't hide the submenu by clicking on the menu
    // button again.


    if (hasParentMenu) {
      state.show();
    }
  });

  if (hasParentMenu) {
    // On Safari, VO+Space triggers a click twice on native button elements
    // with role menuitem (https://bugs.webkit.org/show_bug.cgi?id=228318).
    // So, if the menu button is rendered within a menu, we need to render it
    // as another element.
    props = _objectSpread({
      as: "div"
    }, props);
  } // We'll use this id to render the aria-labelledby attribute on the menu.


  var id = useId(props.id); // When the menu button is rendered inside another menu, we set the role
  // attribute here so it doesn't get overridden by the button component with
  // role="button".

  var role = hasParentMenu || parentIsMenuBar ? getPopupItemRole(parentMenu == null ? void 0 : parentMenu.contentElement, "menuitem") : undefined;
  props = _objectSpread(_objectSpread({
    id: id,
    role: role,
    "aria-haspopup": getPopupRole(state.contentElement, "menu")
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onClick: onClick
  });
  props = useHovercardAnchor(_objectSpread(_objectSpread({
    state: state,
    focusable: focusable,
    accessibleWhenDisabled: accessibleWhenDisabled
  }, props), {}, {
    showOnHover: function showOnHover(event) {
      if (typeof _showOnHover === "function") return _showOnHover(event);
      if (_showOnHover != null) return _showOnHover;
      if (hasParentMenu) return true;
      return parentIsMenuBar && hasExpandedMenuButton(parentMenuBar.items);
    }
  }));
  props = usePopoverDisclosure(_objectSpread({
    state: state,
    toggleOnClick: !hasParentMenu,
    focusable: focusable,
    accessibleWhenDisabled: accessibleWhenDisabled
  }, props));
  props = useCompositeTypeahead(_objectSpread({
    state: state,
    typeahead: parentIsMenuBar
  }, props));
  return props;
});
/**
 * A component that renders a menu button that triggers a dropdown menu.
 * Usually, this is rendered as a native `button` element, but if it's a submenu
 * button rendered as a menu item inside another menu, it'll be rendered as a
 * `div`.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

var MenuButton = createComponent(function (props) {
  var htmlProps = useMenuButton(props);
  return createElement("button", htmlProps);
});
export { MenuButton, useMenuButton };